import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Users, LayoutGrid, AlertTriangle, Mail, Calendar, MapPin, Globe, Phone, Settings } from 'lucide-react';
import api from '../../api/axios';
import { UnitsTab, ResidentsTab } from './SocietyDataPage';
import { PLAN_STYLE, STATUS_STYLE, type SocietyPlan, type SocietyStatus } from '../../types/societyAdmin';

/* ── Types ───────────────────────────────────────────────────────────────── */

interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  lastLoginAt?: string | null;
}

interface ResidentContactPolicy {
  requireEmail: boolean;
  requirePhone: boolean;
}

interface AdminSocietyDetail {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country: string;
  plan: SocietyPlan;
  status: SocietyStatus;
  unitCount: number;
  blockCount: number;
  createdAt: string;
  adminUser?: AdminUser;
}

type Envelope<T> = { success?: boolean; data?: T } | T;
function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data?: T })?.data ?? (raw as T);
}

/* ── Badge helpers ───────────────────────────────────────────────────────── */

const Badge: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
  <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, color, background: bg }}>
    {label}
  </span>
);

const Toggle: React.FC<{ checked: boolean; onChange: () => void; disabled?: boolean }> = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    onClick={onChange}
    disabled={disabled}
    style={{
      width: 40, height: 22, borderRadius: 99, border: 'none', flexShrink: 0,
      background: checked ? 'linear-gradient(135deg, #1A6CF6, #6D28D9)' : 'var(--nx-border)',
      position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      transition: 'background 0.15s',
    }}
  >
    <span style={{
      position: 'absolute', top: 3, left: checked ? 20 : 3,
      width: 16, height: 16, borderRadius: '50%', background: '#fff',
      transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    }} />
  </button>
);

const fmt = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const Spin: React.FC = () => (
  <span style={{
    display: 'inline-block', width: 24, height: 24,
    border: '2px solid var(--nx-border)', borderTopColor: '#1A6CF6',
    borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite',
  }} />
);

/* ── Stat card ───────────────────────────────────────────────────────────── */

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  accent?: string;
}> = ({ icon, label, value, accent = '#1A6CF6' }) => (
  <div style={{
    background: '#fff',
    borderRadius: 12,
    border: '1px solid var(--nx-border)',
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  }}>
    <div style={{
      width: 44,
      height: 44,
      borderRadius: 10,
      background: `${accent}18`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: accent,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', fontFamily: 'Syne, sans-serif', lineHeight: 1.1 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

/* ── InfoRow ─────────────────────────────────────────────────────────────── */

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--nx-border)' }}>
    <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}>{icon}</span>
    <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', width: 100, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 13, color: 'var(--nx-text-primary)', fontWeight: 500 }}>{value}</span>
  </div>
);

/* ── SocietyDetailPage ───────────────────────────────────────────────────── */

const SocietyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [society,     setSociety]     = useState<AdminSocietyDetail | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [confirmKind, setConfirmKind] = useState<'suspend' | 'reactivate' | null>(null);
  const [actioning,   setActioning]   = useState(false);
  const [actionError, setActionError] = useState('');
  const [dataTab,     setDataTab]     = useState<'units' | 'residents'>('units');
  const [policy,       setPolicy]       = useState<ResidentContactPolicy | null>(null);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError,  setPolicyError]  = useState('');

  useEffect(() => {
    if (!id) return;
    api.get(`/api/v1/admin/societies/${id}`)
      .then(res => setSociety(unwrap<AdminSocietyDetail>(res.data)))
      .catch(() => setError('Failed to load society details. Please go back and try again.'))
      .finally(() => setLoading(false));
    api.get(`/api/v1/admin/societies/${id}/resident-contact-policy`)
      .then(res => setPolicy(unwrap<ResidentContactPolicy>(res.data)))
      .catch(() => {});
  }, [id]);

  const togglePolicy = async (field: keyof ResidentContactPolicy) => {
    if (!policy) return;
    const next = { ...policy, [field]: !policy[field] };
    setPolicy(next);
    setPolicySaving(true);
    setPolicyError('');
    try {
      await api.patch(`/api/v1/admin/societies/${id}/resident-contact-policy`, {
        requireEmail: next.requireEmail,
        requirePhone: next.requirePhone,
      });
    } catch {
      setPolicy(policy);
      setPolicyError('Failed to update setting. Please try again.');
    } finally {
      setPolicySaving(false);
    }
  };

  const handleAction = async () => {
    if (!society || !confirmKind) return;
    setActioning(true);
    setActionError('');
    try {
      await api.patch(`/api/v1/admin/societies/${id}/${confirmKind}`);
      setSociety(s => s ? { ...s, status: confirmKind === 'suspend' ? 'SUSPENDED' : 'ACTIVE' } : s);
      setConfirmKind(null);
    } catch {
      setActionError('Action failed. Please try again.');
    } finally { setActioning(false); }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Spin />
      </div>
    );
  }

  /* ── Error ── */
  if (error || !society) {
    return (
      <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32 }}>
        <button
          onClick={() => navigate('/admin/societies')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--nx-text-secondary)', fontSize: 13, cursor: 'pointer', marginBottom: 24, fontFamily: 'var(--font-body)' }}
        >
          <ArrowLeft size={15} /> Back to Societies
        </button>
        <p style={{ color: 'var(--nx-red)', fontSize: 14 }}>{error || 'Society not found.'}</p>
      </div>
    );
  }

  const statusStyle = STATUS_STYLE[society.status] ?? { color: '#64748B', bg: 'rgba(100,116,139,0.1)', label: society.status ?? 'Unknown' };
  const planStyle   = PLAN_STYLE[society.plan] ?? { color: '#64748B', bg: 'rgba(100,116,139,0.1)', label: society.plan ?? 'Unknown' };

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>

      {/* ── Back nav ── */}
      <button
        onClick={() => navigate('/admin/societies')}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--nx-text-secondary)', fontSize: 13, cursor: 'pointer', marginBottom: 20, fontFamily: 'var(--font-body)', padding: 0 }}
      >
        <ArrowLeft size={15} /> Back to Societies
      </button>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em' }}>
              {society.name}
            </h1>
            <Badge label={statusStyle.label} color={statusStyle.color} bg={statusStyle.bg} />
            <Badge label={planStyle.label} color={planStyle.color} bg={planStyle.bg} />
          </div>
          {society.city && (
            <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <MapPin size={13} />
              {[society.city, society.state, society.country].filter(Boolean).join(', ')}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {society.status === 'ACTIVE' ? (
            <button
              onClick={() => { setActionError(''); setConfirmKind('suspend'); }}
              style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)', color: '#DC2626', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Suspend Society
            </button>
          ) : society.status === 'SUSPENDED' ? (
            <button
              onClick={() => { setActionError(''); setConfirmKind('reactivate'); }}
              style={{ padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.06)', color: '#059669', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)' }}
            >
              Reactivate Society
            </button>
          ) : null}
        </div>
      </div>

      {/* ── Quick stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon={<LayoutGrid size={20} />} label="Total Units"  value={society.unitCount}  accent="#1A6CF6" />
        <StatCard icon={<Building2  size={20} />} label="Blocks"       value={society.blockCount} accent="#6D28D9" />
        <StatCard icon={<Calendar   size={20} />} label="Member since" value={fmt(society.createdAt)} accent="#0891B2" />
      </div>

      {/* ── Two-column: Info + Admin user ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>

        {/* Society info card */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 4 }}>
            Society Info
          </h3>
          <div style={{ marginTop: 12 }}>
            <InfoRow icon={<Building2 size={14} />}  label="Name"    value={society.name} />
            <InfoRow icon={<Building2 size={14} />}  label="Society ID" value={<span style={{ fontFamily: 'monospace', fontSize: 11 }}>{society.id}</span>} />
            {society.address && (
              <InfoRow icon={<MapPin size={14} />}   label="Address" value={society.address} />
            )}
            <InfoRow icon={<MapPin size={14} />}     label="City"    value={society.city    ?? '—'} />
            <InfoRow icon={<MapPin size={14} />}     label="State"   value={society.state   ?? '—'} />
            <InfoRow icon={<Globe  size={14} />}     label="Country" value={society.country ?? '—'} />
            <InfoRow icon={<Calendar size={14} />}   label="Created" value={fmt(society.createdAt)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0' }}>
              <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}><Building2 size={14} /></span>
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', width: 100, flexShrink: 0 }}>Plan</span>
              <Badge label={society.plan.toUpperCase()} color={planStyle.color} bg={planStyle.bg} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0' }}>
              <span style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }}><Globe size={14} /></span>
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', width: 100, flexShrink: 0 }}>Status</span>
              <Badge label={statusStyle.label} color={statusStyle.color} bg={statusStyle.bg} />
            </div>
          </div>
        </div>

        {/* Admin user card */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 4 }}>
            Society Admin
          </h3>
          {society.adminUser ? (
            <div style={{ marginTop: 16 }}>
              {/* Avatar + name row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', background: 'rgba(26,108,246,0.04)', borderRadius: 10, marginBottom: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #1A6CF6, #6D28D9)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 16, fontWeight: 700, flexShrink: 0,
                  fontFamily: 'Syne, sans-serif',
                }}>
                  {society.adminUser.fullName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nx-text-primary)' }}>
                    {society.adminUser.fullName}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                    Society Administrator
                  </div>
                </div>
              </div>
              <InfoRow icon={<Mail     size={14} />} label="Email"      value={society.adminUser.email} />
              <InfoRow icon={<Users    size={14} />} label="User ID"    value={<span style={{ fontFamily: 'monospace', fontSize: 11 }}>{society.adminUser.id}</span>} />
              <InfoRow icon={<Calendar size={14} />} label="Last login" value={fmt(society.adminUser.lastLoginAt)} />
            </div>
          ) : (
            <div style={{ marginTop: 20, padding: '24px 16px', textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
              <Users size={28} style={{ opacity: 0.3, display: 'block', margin: '0 auto 10px' }} />
              No admin user assigned yet.
            </div>
          )}
        </div>
      </div>

      {/* ── Resident contact policy ── */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', padding: '20px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginTop: 16 }}>
        <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Settings size={15} /> Resident Contact Requirements
        </h3>
        <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
          Control whether email and phone are mandatory when this society adds a resident.
        </p>
        {policy ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0', borderBottom: '1px solid var(--nx-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Mail size={14} style={{ color: 'var(--nx-text-muted)' }} />
                <span style={{ fontSize: 13, color: 'var(--nx-text-primary)' }}>Require email</span>
              </div>
              <Toggle checked={policy.requireEmail} onChange={() => togglePolicy('requireEmail')} disabled={policySaving} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Phone size={14} style={{ color: 'var(--nx-text-muted)' }} />
                <span style={{ fontSize: 13, color: 'var(--nx-text-primary)' }}>Require phone</span>
              </div>
              <Toggle checked={policy.requirePhone} onChange={() => togglePolicy('requirePhone')} disabled={policySaving} />
            </div>
            {policyError && <p style={{ fontSize: 12, color: 'var(--nx-red)', marginTop: 10 }}>{policyError}</p>}
          </div>
        ) : (
          <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Loading settings…</p>
        )}
      </div>

      {/* ── Apartment data: units & residents ── */}
      <div style={{ marginTop: 28 }}>
        <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 16, fontWeight: 800, color: 'var(--nx-text-primary)', marginBottom: 4 }}>
          Apartment Data
        </h3>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 16 }}>
          Units, blocks, and residents for this society, including main contact information.
        </p>

        <div style={{ display: 'flex', gap: 6, background: '#fff', border: '1px solid var(--nx-border)', borderRadius: 10, padding: 6, width: 'fit-content', marginBottom: 16 }}>
          <button
            onClick={() => setDataTab('units')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
              color: dataTab === 'units' ? '#fff' : 'var(--nx-text-secondary)',
              background: dataTab === 'units' ? 'linear-gradient(135deg, #1A6CF6, #6D28D9)' : 'transparent',
            }}
          >
            <LayoutGrid size={14} /> Units
          </button>
          <button
            onClick={() => setDataTab('residents')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-body)',
              color: dataTab === 'residents' ? '#fff' : 'var(--nx-text-secondary)',
              background: dataTab === 'residents' ? 'linear-gradient(135deg, #1A6CF6, #6D28D9)' : 'transparent',
            }}
          >
            <Users size={14} /> Residents
          </button>
        </div>

        {id && (dataTab === 'units' ? <UnitsTab societyId={id} /> : <ResidentsTab societyId={id} contactPolicy={policy} />)}
      </div>

      {/* ── Confirm dialog ── */}
      {confirmKind && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setConfirmKind(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 28px', maxWidth: 420, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: confirmKind === 'suspend' ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <AlertTriangle size={20} style={{ color: confirmKind === 'suspend' ? '#DC2626' : '#059669' }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 5, fontFamily: 'Syne, sans-serif' }}>
                  {confirmKind === 'suspend' ? 'Suspend' : 'Reactivate'} "{society.name}"?
                </p>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                  {confirmKind === 'suspend'
                    ? 'All users of this society will lose access immediately.'
                    : 'All users of this society will regain access immediately.'}
                </p>
              </div>
            </div>
            {actionError && (
              <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{actionError}</p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setConfirmKind(null); setActionError(''); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={actioning}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: confirmKind === 'suspend'
                    ? 'linear-gradient(135deg, #EF4444, #DC2626)'
                    : 'linear-gradient(135deg, #10B981, #059669)',
                  color: '#fff', cursor: actioning ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: actioning ? 0.7 : 1,
                  fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {actioning && (
                  <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                )}
                {actioning ? 'Processing…' : confirmKind === 'suspend' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocietyDetailPage;
